/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { readRetryAfterMs } from "../retrymechanism/silentRetryOptions";
import {
  getHttpStatus,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_UNAUTHORIZED,
  isForbiddenError,
  isUnauthorizedError
} from "../utility/httpStatus";
import type { IProvisioningStrategy } from "./IProvisioningStrategy";
// Concrete module, not the `auth` barrel — see the note in baseInteractor.ts.
import { TokenManager } from "./TokenManager";
import { getTokenGeneration } from "./tokenGeneration";

const TAG: string = "auth/AuthRejectionArbiter";

/**
 * How long a definitive DPS failure (a 4xx without `Retry-After`) is replayed
 * from memory instead of re-running DPS.
 */
export const PROVISION_FAILURE_COOLDOWN_MS: number = 5 * 60 * 1000;

/** Upper bound on a `Retry-After`-driven cooldown, so a bad header cannot wedge DPS. */
export const PROVISION_FAILURE_MAX_COOLDOWN_MS: number = 60 * 60 * 1000;

/** A DPS failure being replayed until `untilMs`, for the session it hit. */
interface TProvisionFailure {
  error: unknown;
  untilMs: number;
  /** The token generation it failed under — a new session starts clean. */
  generation: number;
}

/**
 * How long to hold a DPS failure, in ms; 0 means do not hold it.
 *
 * - `Retry-After` present — the server's own schedule (DPS `403
 *   NOT_AUTHORIZED` + `Retry-After` is "account suspended, retry later"),
 *   capped at {@link PROVISION_FAILURE_MAX_COOLDOWN_MS}.
 * - any other 4xx — definitive (`TOO_MANY_DEVICES`, `ACCOUNT_DATA_NOT_FOUND`,
 *   …): re-sending the same request cannot succeed, so
 *   {@link PROVISION_FAILURE_COOLDOWN_MS}.
 * - 5xx, timeouts, transport failures — transient, already retried by the
 *   strategy's silent ladder; the next 403 is free to try again.
 */
function provisionFailureCooldownMs(error: unknown): number {
  const retryAfterMs: number | null = readRetryAfterMs(error);
  if (retryAfterMs !== null && retryAfterMs > 0) {
    return Math.min(retryAfterMs, PROVISION_FAILURE_MAX_COOLDOWN_MS);
  }
  const status: number = getHttpStatus(error);
  return status >= 400 && status < 500 ? PROVISION_FAILURE_COOLDOWN_MS : 0;
}

/**
 * What `GET /secure` said about the session.
 *
 * `/secure` is the arbiter because it needs nothing but a valid, provisioned
 * domain token — a 401 or 403 from it is about the session, never about one
 * service's own rules.
 */
export enum SecureProbeVerdict {
  /** 200 — token valid and provisioned; the CC API's 401/403 was its own. */
  SessionValid = "sessionValid",
  /** 401 — the domain token is invalid; refresh it. */
  TokenInvalid = "tokenInvalid",
  /** 403 — the token is valid but provisioning is not; run DPS. */
  NotProvisioned = "notProvisioned",
  /** Anything else — TiVo unreachable or failing; nothing to recover. */
  Inconclusive = "inconclusive"
}

/** Performs `GET /secure` with the active context's token. */
export type TSecureProbe = () => Promise<void>;

/**
 * Decides what a 401 or 403 from a CloudCore API means, by asking `/secure`.
 *
 * A CC API's 401/403 is not proof the session is bad: a service can refuse a
 * resource for reasons of its own. So before the session is touched, `/secure`
 * is called and its answer — not the CC API's — picks the recovery:
 *
 * | `/secure` | Meaning                       | Recovery                  |
 * |-----------|-------------------------------|---------------------------|
 * | 200       | CC API's rejection is its own | none — original error     |
 * | 401       | domain token invalid          | invalidate token, replay  |
 * | 403       | device not provisioned        | DPS, replay               |
 * | other     | inconclusive                  | CC 401: refresh, replay;  |
 * |           |                               | CC 403: original error    |
 *
 * Only 401 and 403 are arbitrated. Every other failure is left alone: `/secure`
 * could say nothing about it the failing call has not already said.
 *
 * Concurrent rejections share one probe and one DPS run, so a screen whose
 * calls all fail together costs one `/secure`, not one per call.
 *
 * A definitive DPS failure is held for a cooldown
 * ({@link provisionFailureCooldownMs}): until it lapses, a `/secure` 403
 * re-throws that failure instead of re-running DPS, so a device that cannot be
 * provisioned fails fast and does not send DPS a request per CC call. `/secure`
 * is still asked each time — it is what notices the problem has cleared.
 *
 * The probe and the provisioning strategy are registered from the composition
 * root: both are built on interactors, which extend the base interactor that
 * calls this — importing them here would be circular.
 */
export class AuthRejectionArbiter {
  private static mProbe: TSecureProbe | null = null;
  private static mProvisioning: IProvisioningStrategy | null = null;
  private static mProbeInFlight: Promise<SecureProbeVerdict> | null = null;
  private static mProvisionInFlight: Promise<void> | null = null;
  private static mProvisionFailure: TProvisionFailure | null = null;

  public static setSecureProbe(probe: TSecureProbe): void {
    AuthRejectionArbiter.mProbe = probe;
  }

  public static setProvisioningStrategy(strategy: IProvisioningStrategy): void {
    AuthRejectionArbiter.mProvisioning = strategy;
  }

  /** True for the failures this class arbitrates: HTTP 401 and 403. */
  public static isAuthRejection(error: unknown): boolean {
    return isUnauthorizedError(error) || isForbiddenError(error);
  }

  /**
   * Arbitrates one rejected call.
   *
   * @param methodName - The rejected call, for logging.
   * @param error - Its 401/403.
   * @param generationAtStart - {@link getTokenGeneration} read before the call
   *        was sent.
   * @returns true to replay the call once; false to re-throw `error`.
   * @throws The DPS error when re-provisioning fails.
   */
  public static async resolve(
    methodName: string,
    error: unknown,
    generationAtStart: number
  ): Promise<boolean> {
    const status: number = getHttpStatus(error);

    // The rejected token was already replaced by a concurrent recovery, so the
    // rejection is stale: replay with the new token rather than probing.
    if (getTokenGeneration() !== generationAtStart) {
      Log.d(
        TAG,
        methodName + "() — " + status + " on a superseded token, replaying"
      );
      return true;
    }

    let verdict: SecureProbeVerdict = await AuthRejectionArbiter.probe();
    Log.w(TAG, methodName + "() — " + status + ", /secure says " + verdict);
    if (
      verdict === SecureProbeVerdict.Inconclusive &&
      isUnauthorizedError(error)
    ) {
      // /secure could not answer (unreachable, 5xx, not registered). A 401 is
      // then taken at its word, as before arbitration: a refresh is cheap,
      // and surfacing a 401 for a merely expired token is not. A 403 is left
      // alone — DPS on a guess is neither.
      verdict = SecureProbeVerdict.TokenInvalid;
    }

    switch (verdict) {
      case SecureProbeVerdict.TokenInvalid:
        // Another rejection's recovery may have refreshed while this one
        // waited on the shared probe; expiring that fresh token would cost a
        // second, needless refresh.
        if (getTokenGeneration() === generationAtStart) {
          await TokenManager.invalidate();
        }
        return true;
      case SecureProbeVerdict.NotProvisioned:
        return AuthRejectionArbiter.reprovision();
      case SecureProbeVerdict.SessionValid:
        // Provisioning is evidently fine now, whatever DPS said earlier.
        AuthRejectionArbiter.mProvisionFailure = null;
        return false;
      case SecureProbeVerdict.Inconclusive:
      default:
        return false;
    }
  }

  /**
   * Calls `/secure`, joining a probe already in flight. Inconclusive when no
   * probe is registered.
   */
  private static probe(): Promise<SecureProbeVerdict> {
    const probe: TSecureProbe | null = AuthRejectionArbiter.mProbe;
    if (probe === null) {
      return Promise.resolve(SecureProbeVerdict.Inconclusive);
    }

    const pending: Promise<SecureProbeVerdict> | null =
      AuthRejectionArbiter.mProbeInFlight;
    if (pending !== null) {
      return pending;
    }

    const run: Promise<SecureProbeVerdict> = probe()
      .then((): SecureProbeVerdict => SecureProbeVerdict.SessionValid)
      .catch((probeError: unknown): SecureProbeVerdict => {
        const status: number = getHttpStatus(probeError);
        if (status === HTTP_STATUS_UNAUTHORIZED) {
          return SecureProbeVerdict.TokenInvalid;
        }
        if (status === HTTP_STATUS_FORBIDDEN) {
          return SecureProbeVerdict.NotProvisioned;
        }
        Log.e(TAG, "probe() /secure failed without a verdict", probeError);
        return SecureProbeVerdict.Inconclusive;
      })
      .finally((): void => {
        AuthRejectionArbiter.mProbeInFlight = null;
      });
    AuthRejectionArbiter.mProbeInFlight = run;
    return run;
  }

  /**
   * Runs DPS, joining a run already in flight — or, inside a cooldown,
   * re-throws the failure being held instead of running it.
   *
   * @returns true once provisioned; false when no strategy is registered.
   * @throws The DPS failure, fresh or held.
   */
  private static async reprovision(): Promise<boolean> {
    const strategy: IProvisioningStrategy | null =
      AuthRejectionArbiter.mProvisioning;
    if (strategy === null) {
      Log.e(TAG, "reprovision() no provisioning strategy registered");
      return false;
    }

    const held: TProvisionFailure | null =
      AuthRejectionArbiter.mProvisionFailure;
    if (
      held !== null &&
      held.generation === getTokenGeneration() &&
      Date.now() < held.untilMs
    ) {
      Log.w(TAG, "reprovision() DPS in cooldown, re-throwing its last failure");
      throw held.error;
    }

    let pending: Promise<void> | null = AuthRejectionArbiter.mProvisionInFlight;
    if (pending === null) {
      pending = AuthRejectionArbiter.runProvisioning(strategy);
      AuthRejectionArbiter.mProvisionInFlight = pending;
    }
    await pending;
    return true;
  }

  /** One DPS run, recording its outcome for the cooldown. */
  private static runProvisioning(
    strategy: IProvisioningStrategy
  ): Promise<void> {
    const generation: number = getTokenGeneration();
    return strategy
      .provision()
      .then((): void => {
        AuthRejectionArbiter.mProvisionFailure = null;
      })
      .catch((provisionError: unknown): never => {
        const cooldownMs: number = provisionFailureCooldownMs(provisionError);
        AuthRejectionArbiter.mProvisionFailure =
          cooldownMs > 0
            ? {
                error: provisionError,
                untilMs: Date.now() + cooldownMs,
                generation: generation
              }
            : null;
        if (cooldownMs > 0) {
          Log.w(
            TAG,
            "reprovision() DPS failed definitively, holding for " +
              cooldownMs +
              "ms"
          );
        }
        throw provisionError;
      })
      .finally((): void => {
        AuthRejectionArbiter.mProvisionInFlight = null;
      });
  }

  /** Test-only: forgets registrations, in-flight work and held failures. */
  public static __resetForTest(): void {
    AuthRejectionArbiter.mProbe = null;
    AuthRejectionArbiter.mProvisioning = null;
    AuthRejectionArbiter.mProbeInFlight = null;
    AuthRejectionArbiter.mProvisionInFlight = null;
    AuthRejectionArbiter.mProvisionFailure = null;
  }
}
